// Desktop row for `/russia-rates`. Mirrors the China request-workspace
// row interaction (full-row hover + click + keyboard) while keeping
// Russia-specific cells (ID, type, status, name, manager, start cities,
// end address, primary bid, created date).
//
// URGENT-typed rows carry a `--status-error-bg` tint that shows when
// the row is not hovered; hover state takes visual precedence so the
// user still gets the standard interaction feedback.

import type { CSSProperties } from 'react';
import { RussiaRequestType, type RussiaRequest } from '@magimp/shared';

import { useInteractiveRow } from '../../../ui/index.js';
import { NotificationDot } from '../../notifications/index.js';
import { RequestStatusPill } from '../../requests/display/index.js';
import { RussiaRequestPrimaryBidCell } from './RussiaRequestPrimaryBidCell.js';
import { RussiaRequestTypeChip } from './RussiaRequestTypeChip.js';
import { StartCityChips } from './StartCityChips.js';

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

const BASE_CELL: CSSProperties = {
  padding: '0 10px',
  minHeight: 42,
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid var(--border-2)',
  cursor: 'pointer',
  transition: 'background var(--dur-1) var(--ease-out)',
};

interface RussiaRequestRowProps {
  row: RussiaRequest;
  onOpen: () => void;
  language: string;
  notificationCount?: number;
}

export function RussiaRequestRow({
  row,
  onOpen,
  language,
  notificationCount,
}: RussiaRequestRowProps) {
  const { background, handlers } = useInteractiveRow({ selected: false, onActivate: onOpen });
  const isUrgent = row.type === RussiaRequestType.URGENT;
  const cellBg =
    background !== 'transparent' ? background : isUrgent ? 'var(--status-error-bg)' : 'transparent';

  const cell = (extra?: CSSProperties): CSSProperties => ({
    ...BASE_CELL,
    background: cellBg,
    ...extra,
  });

  return (
    <div
      style={{ display: 'contents' }}
      data-testid={`russia-requests-row-${row.id}`}
      data-urgent={isUrgent ? 'true' : undefined}
    >
      <div style={cell({ fontFamily: 'var(--font-mono)', fontSize: 12 })} {...handlers}>
        <span
          data-testid={`russia-requests-row-link-${row.id}`}
          style={{ color: 'var(--accent-1-press)' }}
        >
          {row.id}
        </span>
      </div>
      <div style={cell()} {...handlers}>
        <RussiaRequestTypeChip type={row.type} />
      </div>
      <div style={cell()} {...handlers}>
        <RequestStatusPill status={row.status} size="sm" />
      </div>
      <div style={cell({ fontWeight: 500, minWidth: 0, gap: 8 })} {...handlers}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--fg-1)',
            flex: '1 1 0',
            minWidth: 0,
          }}
          title={row.name}
        >
          {row.name}
        </span>
        {notificationCount !== undefined && notificationCount > 0 ? (
          <NotificationDot count={notificationCount} />
        ) : null}
      </div>
      <div style={cell({ minWidth: 0 })} {...handlers}>
        <span
          data-testid={`russia-requests-row-manager-${row.id}`}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 12,
            color: 'var(--fg-2)',
          }}
        >
          {row.manager}
        </span>
      </div>
      <div style={cell({ minWidth: 0 })} {...handlers}>
        <StartCityChips
          startCities={row.startCities}
          testIdPrefix={`russia-requests-row-start-cities-${row.id}`}
        />
      </div>
      <div style={cell({ minWidth: 0 })} {...handlers}>
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--fg-2)',
            fontSize: 12,
          }}
          title={row.endAddress}
        >
          {row.endAddress}
        </span>
      </div>
      <div
        style={cell({ minWidth: 0 })}
        data-testid={`russia-requests-row-primary-bid-${row.id}`}
        {...handlers}
      >
        <RussiaRequestPrimaryBidCell
          summary={row.primaryBidSummary}
          rowId={row.id}
          i18nLanguage={language}
        />
      </div>
      <div
        style={cell({
          color: 'var(--fg-3)',
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        })}
        {...handlers}
      >
        {formatDate(row.createdAt)}
      </div>
    </div>
  );
}
